import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import LoginPage from './pages/LoginPage'
import DashboardLayout from './components/layouts/DashboardLayout'
import Dashboard from './pages/Dashboard'

// Lazy load pages for code splitting
const AttendancePage = lazy(() => import('./pages/AttendancePage'))
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'))
const EmployeeEditPage = lazy(() => import('./pages/EmployeeEditPage'))
const DepartmentsPage = lazy(() => import('./pages/DepartmentsPage'))
const PositionsPage = lazy(() => import('./pages/PositionsPage'))
const LeavePage = lazy(() => import('./pages/LeavePage'))
const LeaveTypesPage = lazy(() => import('./pages/LeaveTypesPage'))
const PayrollPage = lazy(() => import('./pages/PayrollPage'))
const EmployeeSalarySetupPage = lazy(() => import('./pages/EmployeeSalarySetupPage'))
const TaxSettingsPage = lazy(() => import('./pages/TaxSettingsPage'))
const BankTransferPage = lazy(() => import('./pages/BankTransferPage'))
const PerformancePage = lazy(() => import('./pages/PerformancePage'))
const CyclesPage = lazy(() => import('./pages/performance/CyclesPage'))
const MyReviewPage = lazy(() => import('./pages/performance/MyReviewPage'))
const TeamReviewsPage = lazy(() => import('./pages/performance/TeamReviewsPage'))
const KpisPage = lazy(() => import('./pages/performance/KpisPage'))
const StatisticsPage = lazy(() => import('./pages/performance/StatisticsPage'))
const CalibrationPage = lazy(() => import('./pages/performance/CalibrationPage'))
const GoalsPage = lazy(() => import('./pages/performance/GoalsPage'))
const BonusRulesPage = lazy(() => import('./pages/performance/BonusRulesPage'))
const NineBoxPage = lazy(() => import('./pages/performance/NineBoxPage'))
const PeerSummaryPage = lazy(() => import('./pages/performance/PeerSummaryPage'))
const ScheduledReportsPage = lazy(() => import('./pages/ScheduledReportsPage'))
const SchedulingPage = lazy(() => import('./pages/SchedulingPage'))
const CalendarPage = lazy(() => import('./pages/CalendarPage'))
const TrainingPage = lazy(() => import('./pages/TrainingPage'))
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const MyPayslipPage = lazy(() => import('./pages/MyPayslipPage'))
const MyAttendancePage = lazy(() => import('./pages/MyAttendancePage'))
const MyRewardsPage = lazy(() => import('./pages/MyRewardsPage'))
const OrgChartPage = lazy(() => import('./pages/OrgChartPage'))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const RecruitmentPage = lazy(() => import('./pages/RecruitmentPage'))
const OfferLettersPage = lazy(() => import('./pages/OfferLettersPage'))
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'))
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'))
const MeetingRoomsPage = lazy(() => import('./pages/MeetingRoomsPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const ApprovalSettingsPage = lazy(() => import('./pages/ApprovalSettingsPage'))
const OvertimePage = lazy(() => import('./pages/OvertimePage'))
const AssetsPage = lazy(() => import('./pages/AssetsPage'))
const OffboardingPage = lazy(() => import('./pages/OffboardingPage'))
const TravelPage = lazy(() => import('./pages/TravelPage'))
const HealthCheckupsPage = lazy(() => import('./pages/HealthCheckupsPage'))
const PIPPage = lazy(() => import('./pages/PIPPage'))
const DevelopmentPage = lazy(() => import('./pages/DevelopmentPage'))
const MyHubPage = lazy(() => import('./pages/MyHubPage'))
const MyProfilePage = lazy(() => import('./pages/MyProfilePage'))
const SalaryBandsPage = lazy(() => import('./pages/SalaryBandsPage'))
const SuccessionPage = lazy(() => import('./pages/SuccessionPage'))
const SurveysPage = lazy(() => import('./pages/SurveysPage'))
const EmployeeDirectoryPage = lazy(() => import('./pages/EmployeeDirectoryPage'))
const RewardsPenaltiesPage = lazy(() => import('./pages/RewardsPenaltiesPage'))
const CompanyPoliciesPage = lazy(() => import('./pages/CompanyPoliciesPage'))
const HolidayCalendarPage = lazy(() => import('./pages/HolidayCalendarPage'))
const InsurancePage = lazy(() => import('./pages/InsurancePage'))
// Previously orphaned pages — now restored to routing
const AttendanceAnomaliesPage = lazy(() => import('./pages/AttendanceAnomaliesPage'))
const AttendanceCorrectionsPage = lazy(() => import('./pages/AttendanceCorrectionsPage'))
const SwapRequestsPage = lazy(() => import('./pages/SwapRequestsPage'))
const ShiftTypesPage = lazy(() => import('./pages/ShiftTypesPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const GovernmentFormsPage = lazy(() => import('./pages/GovernmentFormsPage'))
const InsuranceChangesPage = lazy(() => import('./pages/InsuranceChangesPage'))
const BenefitsPage = lazy(() => import('./pages/BenefitsPage'))
const MyBenefitsPage = lazy(() => import('./pages/MyBenefitsPage'))
const SeverancePage = lazy(() => import('./pages/SeverancePage'))
const TransfersPage = lazy(() => import('./pages/TransfersPage'))
const CareersPage = lazy(() => import('./pages/CareersPage'))
const RbacPage = lazy(() => import('./pages/RbacPage'))
const ProfileChangeRequestsPage = lazy(() => import('./pages/ProfileChangeRequestsPage'))
// Split pages — personal "My*" + manager "*Approvals"
const MyLeavePage = lazy(() => import('./pages/MyLeavePage'))
const LeaveApprovalsPage = lazy(() => import('./pages/LeaveApprovalsPage'))
const MyOvertimePage = lazy(() => import('./pages/MyOvertimePage'))
const OvertimeApprovalsPage = lazy(() => import('./pages/OvertimeApprovalsPage'))
const MyExpensesPage = lazy(() => import('./pages/MyExpensesPage'))
const ExpenseApprovalsPage = lazy(() => import('./pages/ExpenseApprovalsPage'))
const MyTravelPage = lazy(() => import('./pages/MyTravelPage'))
const TravelApprovalsPage = lazy(() => import('./pages/TravelApprovalsPage'))
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'))
// Team (manager view)
const TeamHomePage = lazy(() => import('./pages/TeamHomePage'))
const TeamAttendancePage = lazy(() => import('./pages/TeamAttendancePage'))
const TeamLeaveCalendarPage = lazy(() => import('./pages/TeamLeaveCalendarPage'))
const TeamPerformancePage = lazy(() => import('./pages/TeamPerformancePage'))
const TeamMembersPage = lazy(() => import('./pages/TeamMembersPage'))
const TeamDelegationPage = lazy(() => import('./pages/TeamDelegationPage'))

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p className="mt-2 text-gray-600">載入中...</p>
    </div>
  </div>
)

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Role-based Route Component
const RoleRoute = ({ children, roles }) => {
  const { user } = useSelector((state) => state.auth)

  if (!user || (roles && !roles.includes(user.role))) {
    return <Navigate to="/" replace />
  }

  return children
}

const HR_ADMIN = ['hr_manager', 'super_admin', 'company_admin']
const MANAGER_UP = ['department_manager', 'hr_manager', 'super_admin', 'company_admin']

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={
        <Suspense fallback={<PageLoader />}>
          <RegisterPage />
        </Suspense>
      } />
      <Route path="/forgot-password" element={
        <Suspense fallback={<PageLoader />}>
          <ForgotPasswordPage />
        </Suspense>
      } />
      <Route path="/reset-password" element={
        <Suspense fallback={<PageLoader />}>
          <ResetPasswordPage />
        </Suspense>
      } />
      <Route path="/careers" element={
        <Suspense fallback={<PageLoader />}>
          <CareersPage />
        </Suspense>
      } />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        {/* Employee Self-Service */}
        <Route path="my-hub" element={<Suspense fallback={<PageLoader />}><MyHubPage /></Suspense>} />
        <Route path="my-profile" element={<Suspense fallback={<PageLoader />}><MyProfilePage /></Suspense>} />
        <Route path="my-payslip" element={<Suspense fallback={<PageLoader />}><MyPayslipPage /></Suspense>} />
        <Route path="my-attendance" element={<Suspense fallback={<PageLoader />}><MyAttendancePage /></Suspense>} />
        <Route path="my-rewards" element={<Suspense fallback={<PageLoader />}><MyRewardsPage /></Suspense>} />
        <Route path="directory" element={<Suspense fallback={<PageLoader />}><EmployeeDirectoryPage /></Suspense>} />
        <Route path="rewards-penalties" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><RewardsPenaltiesPage /></RoleRoute></Suspense>} />
        <Route path="company-policies" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><CompanyPoliciesPage /></RoleRoute></Suspense>} />
        <Route path="holiday-calendar" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><HolidayCalendarPage /></RoleRoute></Suspense>} />
        <Route path="insurance" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><InsurancePage /></RoleRoute></Suspense>} />
        <Route path="attendance-anomalies" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><AttendanceAnomaliesPage /></RoleRoute></Suspense>} />
        <Route path="attendance-corrections" element={<Suspense fallback={<PageLoader />}><AttendanceCorrectionsPage /></Suspense>} />
        <Route path="swap-requests" element={<Suspense fallback={<PageLoader />}><SwapRequestsPage /></Suspense>} />
        <Route path="shift-types" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><ShiftTypesPage /></RoleRoute></Suspense>} />
        <Route path="notifications" element={<Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense>} />
        <Route
          path="attendance"
          element={
            <Suspense fallback={<PageLoader />}>
              <AttendancePage />
            </Suspense>
          }
        />
        <Route
          path="employees"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={MANAGER_UP}><EmployeesPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="employees/:id/edit"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={MANAGER_UP}><EmployeeEditPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="org-chart"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={MANAGER_UP}><OrgChartPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="departments"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><DepartmentsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="positions"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><PositionsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="leave"
          element={
            <Suspense fallback={<PageLoader />}>
              <LeavePage />
            </Suspense>
          }
        />
        <Route
          path="overtime"
          element={
            <Suspense fallback={<PageLoader />}>
              <OvertimePage />
            </Suspense>
          }
        />
        <Route
          path="leave-types"
          element={
            <Suspense fallback={<PageLoader />}>
              <LeaveTypesPage />
            </Suspense>
          }
        />
        <Route
          path="payroll"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><PayrollPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="salary-setup"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><EmployeeSalarySetupPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="tax-settings"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><TaxSettingsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="bank-transfer"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><BankTransferPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="performance"
          element={
            <Suspense fallback={<PageLoader />}>
              <PerformancePage />
            </Suspense>
          }
        />
        <Route
          path="performance/cycles"
          element={
            <Suspense fallback={<PageLoader />}>
              <CyclesPage />
            </Suspense>
          }
        />
        <Route
          path="performance/kpis"
          element={
            <Suspense fallback={<PageLoader />}>
              <KpisPage />
            </Suspense>
          }
        />
        <Route
          path="performance/my-review"
          element={
            <Suspense fallback={<PageLoader />}>
              <MyReviewPage />
            </Suspense>
          }
        />
        <Route
          path="performance/team-reviews"
          element={
            <Suspense fallback={<PageLoader />}>
              <TeamReviewsPage />
            </Suspense>
          }
        />
        <Route
          path="performance/calibration"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><CalibrationPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route path="performance/goals" element={<Suspense fallback={<PageLoader />}><GoalsPage /></Suspense>} />
        <Route
          path="performance/statistics"
          element={
            <Suspense fallback={<PageLoader />}>
              <StatisticsPage />
            </Suspense>
          }
        />
        <Route
          path="performance/bonus-rules"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><BonusRulesPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="performance/nine-box"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={MANAGER_UP}><NineBoxPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="performance/peer-summary"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={MANAGER_UP}><PeerSummaryPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="scheduled-reports"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><ScheduledReportsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="scheduling"
          element={
            <Suspense fallback={<PageLoader />}>
              <SchedulingPage />
            </Suspense>
          }
        />
        <Route
          path="equipment"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><AssetsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="calendar"
          element={
            <Suspense fallback={<PageLoader />}>
              <CalendarPage />
            </Suspense>
          }
        />
        <Route
          path="training"
          element={
            <Suspense fallback={<PageLoader />}>
              <TrainingPage />
            </Suspense>
          }
        />
        <Route
          path="training/my"
          element={
            <Suspense fallback={<PageLoader />}>
              <TrainingPage />
            </Suspense>
          }
        />
        <Route
          path="expenses"
          element={
            <Suspense fallback={<PageLoader />}>
              <ExpensesPage />
            </Suspense>
          }
        />
        <Route
          path="reports"
          element={
            <Suspense fallback={<PageLoader />}>
              <ReportsPage />
            </Suspense>
          }
        />
        <Route
          path="recruitment"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><RecruitmentPage /></RoleRoute></Suspense>}
        />
        <Route
          path="offer-letters"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><OfferLettersPage /></RoleRoute></Suspense>}
        />
        <Route
          path="announcements"
          element={<Suspense fallback={<PageLoader />}><AnnouncementsPage /></Suspense>}
        />
        <Route
          path="meetings"
          element={<Suspense fallback={<PageLoader />}><MeetingsPage /></Suspense>}
        />
        <Route
          path="meeting-rooms"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><MeetingRoomsPage /></RoleRoute></Suspense>}
        />
        <Route
          path="onboarding"
          element={<Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense>}
        />
        <Route
          path="offboarding-records"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><OffboardingPage /></RoleRoute></Suspense>}
        />
        <Route
          path="business-trips"
          element={<Suspense fallback={<PageLoader />}><TravelPage /></Suspense>}
        />
        <Route
          path="health-checkups"
          element={<Suspense fallback={<PageLoader />}><HealthCheckupsPage /></Suspense>}
        />
        <Route
          path="pips"
          element={<Suspense fallback={<PageLoader />}><PIPPage /></Suspense>}
        />
        <Route
          path="development"
          element={<Suspense fallback={<PageLoader />}><DevelopmentPage /></Suspense>}
        />
        <Route
          path="salary-bands"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><SalaryBandsPage /></RoleRoute></Suspense>}
        />
        <Route
          path="succession-planning"
          element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><SuccessionPage /></RoleRoute></Suspense>}
        />
        <Route
          path="surveys"
          element={<Suspense fallback={<PageLoader />}><SurveysPage /></Suspense>}
        />
        <Route path="government-forms" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><GovernmentFormsPage /></RoleRoute></Suspense>} />
        <Route path="insurance-changes" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><InsuranceChangesPage /></RoleRoute></Suspense>} />
        <Route path="benefits" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><BenefitsPage /></RoleRoute></Suspense>} />
        <Route path="my-benefits" element={<Suspense fallback={<PageLoader />}><MyBenefitsPage /></Suspense>} />
        <Route path="severance" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><SeverancePage /></RoleRoute></Suspense>} />
        <Route path="transfers" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><TransfersPage /></RoleRoute></Suspense>} />
        <Route path="profile-change-requests" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={HR_ADMIN}><ProfileChangeRequestsPage /></RoleRoute></Suspense>} />
        {/* Employee self-service: my change requests (no role restriction — backend auto-filters to own) */}
        <Route path="my-requests" element={<Suspense fallback={<PageLoader />}><ProfileChangeRequestsPage /></Suspense>} />
        {/* Split personal / approval pages */}
        <Route path="my-leave" element={<Suspense fallback={<PageLoader />}><MyLeavePage /></Suspense>} />
        <Route path="approvals/leave" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><LeaveApprovalsPage /></RoleRoute></Suspense>} />
        <Route path="my-overtime" element={<Suspense fallback={<PageLoader />}><MyOvertimePage /></Suspense>} />
        <Route path="approvals/overtime" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><OvertimeApprovalsPage /></RoleRoute></Suspense>} />
        <Route path="my-expenses" element={<Suspense fallback={<PageLoader />}><MyExpensesPage /></Suspense>} />
        <Route path="approvals/expense" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><ExpenseApprovalsPage /></RoleRoute></Suspense>} />
        <Route path="my-travel" element={<Suspense fallback={<PageLoader />}><MyTravelPage /></Suspense>} />
        <Route path="approvals/travel" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TravelApprovalsPage /></RoleRoute></Suspense>} />
        <Route path="approvals" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><ApprovalsPage /></RoleRoute></Suspense>} />
        {/* Team (manager-perspective) */}
        <Route path="team" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamHomePage /></RoleRoute></Suspense>} />
        <Route path="team/attendance" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamAttendancePage /></RoleRoute></Suspense>} />
        <Route path="team/leave-calendar" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamLeaveCalendarPage /></RoleRoute></Suspense>} />
        <Route path="team/performance" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamPerformancePage /></RoleRoute></Suspense>} />
        <Route path="team/members" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamMembersPage /></RoleRoute></Suspense>} />
        <Route path="team/delegation" element={<Suspense fallback={<PageLoader />}><RoleRoute roles={MANAGER_UP}><TeamDelegationPage /></RoleRoute></Suspense>} />
        <Route
          path="audit-logs"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><AuditLogPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="approval-settings"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={HR_ADMIN}><ApprovalSettingsPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="rbac"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleRoute roles={['super_admin', 'company_admin']}><RbacPage /></RoleRoute>
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="subscription"
          element={
            <Suspense fallback={<PageLoader />}>
              <SubscriptionPage />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
    </Routes>
  )
}

export default App
